import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { CanvasElement, Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import { buildLegendGrid } from './map.print.legend';
import { scaleBar } from './map.print.scalebar';
import {
	BOX_COLOR,
	DATE_LABEL_COLOR,
	FOOTER_COLOR,
	HEADING_COLOR,
	LABEL_COLOR,
	SEPARATOR_COLOR,
	VALUE_COLOR,
} from './map.print.theme';

pdfMake.vfs = pdfFonts;

// LETTER landscape, in PDF points.
const PAGE_WIDTH = 792;
const PAGE_HEIGHT = 612;
const MARGIN = 36;

// Branded header band, with a separator line beneath it.
const HEADER_HEIGHT = 48;
const HEADER_Y = MARGIN;
const SEPARATOR_Y = HEADER_Y + HEADER_HEIGHT;
const LOGO_WIDTH = 92;

// Body sits below the separator and above a thin footer reserve.
const BODY_TOP = SEPARATOR_Y + 10;
const BODY_BOTTOM = PAGE_HEIGHT - MARGIN - 8;

const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const PANEL_GUTTER = 12;
const PANEL_PAD = 12;

// The map fills the full content width; a bottom band holds the note (left) and legend (right).
const BOTTOM_BAND_HEIGHT = 96;
const BAND_Y = BODY_BOTTOM - BOTTOM_BAND_HEIGHT;
const NOTE_WIDTH = 200;

const MAP_AREA = {
	x: MARGIN,
	y: BODY_TOP,
	width: CONTENT_WIDTH,
	height: BAND_Y - PANEL_GUTTER - BODY_TOP,
};

/** Width-to-height ratio of the printed map slot; the on-screen capture frame is locked to it so the
 *  capture fills the slot edge-to-edge with no letterboxing. */
export const MAP_AREA_ASPECT = MAP_AREA.width / MAP_AREA.height;

// When neither the legend nor a note is included, the bottom band is dropped and the map fills the body.
const MAP_AREA_FULL = { x: MARGIN, y: BODY_TOP, width: CONTENT_WIDTH, height: BODY_BOTTOM - BODY_TOP };

/** Map slot aspect when there is no bottom band. The capture frame uses this when both legend and
 *  note are excluded. */
export const MAP_AREA_ASPECT_NO_BAND = MAP_AREA_FULL.width / MAP_AREA_FULL.height;

interface Panel {
	x: number;
	y: number;
	width: number;
	height: number;
}

interface BandLayout {
	note: Panel | null;
	legend: Panel | null;
}

/** Lay out the bottom band: note (left) beside legend (right); whichever is alone fills the band. */
function bandLayout(noteIncluded: boolean, legendIncluded: boolean): BandLayout {
	const full = (): Panel => ({ x: MARGIN, y: BAND_Y, width: CONTENT_WIDTH, height: BOTTOM_BAND_HEIGHT });
	if (noteIncluded && legendIncluded) {
		const legendX = MARGIN + NOTE_WIDTH + PANEL_GUTTER;
		return {
			note: { x: MARGIN, y: BAND_Y, width: NOTE_WIDTH, height: BOTTOM_BAND_HEIGHT },
			legend: { x: legendX, y: BAND_Y, width: PAGE_WIDTH - MARGIN - legendX, height: BOTTOM_BAND_HEIGHT },
		};
	}
	return { note: noteIncluded ? full() : null, legend: legendIncluded ? full() : null };
}

const BOX_RADIUS = 4;

/** Which map elements to include in the export; toggled via the print modal's checkboxes. */
export interface MapPrintElements {
	northArrow: boolean;
	scaleBar: boolean;
	legend: boolean;
	collectionLabels: boolean;
	vectorLabels: boolean;
	deptJob: boolean;
	notes: boolean;
}

/** One captured map frame; each becomes a single page in the exported PDF. */
export interface MapPrintPage {
	imageDataUrl: string;
	imageWidth: number;
	imageHeight: number;
	/** Ground metres per pixel of the captured image; drives the true-to-ground scale bar. */
	metersPerImagePixel: number;
	/** Google's live attribution/copyright text, re-drawn since the crop excludes it. */
	attribution: string;
}

export interface MapPrintPdfOptions {
	/** Captured frames, in order; one PDF page each. */
	pages: MapPrintPage[];
	departmentName: string;
	jobName: string;
	note: string;
	/** Raw markup of the ikeGPS logo SVG, or an empty string to omit the logo. */
	logoSvg: string;
	/** User-selected elements to include. */
	elements: MapPrintElements;
}

export function buildAndDownloadPdf(opts: MapPrintPdfOptions): void {
	const { elements } = opts;
	const filename = buildFilename(opts.jobName);

	const noteIncluded = elements.notes && opts.note.trim().length > 0;
	const legendIncluded = elements.legend;
	const band = bandLayout(noteIncluded, legendIncluded);
	const mapArea = noteIncluded || legendIncluded ? MAP_AREA : MAP_AREA_FULL;

	// Each page's content is absolutely positioned; an in-flow page-break marker between pages advances
	// pdfmake's page cursor so the following absolute content lands on the next page.
	const content: Content[] = [];
	opts.pages.forEach((page, index) => {
		if (index > 0) {
			content.push({ text: '', fontSize: 1, pageBreak: 'before' });
		}
		content.push(...pageContent(page, opts, elements, band, mapArea));
	});

	const attribution = unionAttributions(opts.pages);

	pdfMake
		.createPdf({
			pageSize: 'LETTER',
			pageOrientation: 'landscape',
			pageMargins: [MARGIN, MARGIN, MARGIN, MARGIN],
			content,
			footer: (currentPage, pageCount) => ({
				margin: [MARGIN, 4, MARGIN, 0],
				columns: [
					{ text: filename, fontSize: 7, color: FOOTER_COLOR },
					{ text: footerAttribution(attribution), fontSize: 7, color: FOOTER_COLOR, alignment: 'center' },
					{ text: `Page ${currentPage} of ${pageCount}`, fontSize: 7, color: FOOTER_COLOR, alignment: 'right' },
				],
			}),
		} satisfies TDocumentDefinitions)
		.download(filename);
}

/** All the absolutely-positioned content for one page: header, map image, overlays, and bottom band. */
function pageContent(
	page: MapPrintPage,
	opts: MapPrintPdfOptions,
	elements: MapPrintElements,
	band: BandLayout,
	mapArea: FittedMap
): Content[] {
	const fit = fitMap(mapArea, page.imageWidth, page.imageHeight);
	const metersPerPoint = (page.metersPerImagePixel * page.imageWidth) / fit.width;

	return [
		...header(opts, elements.deptJob),
		{ image: page.imageDataUrl, width: fit.width, height: fit.height, absolutePosition: { x: fit.x, y: fit.y } },
		...(elements.northArrow ? northArrow(fit) : []),
		...(elements.scaleBar ? scaleBar(fit, metersPerPoint, Services.UserSettings.units) : []),
		boxes(fit, band),
		...(band.note ? [noteBlock(opts.note, band.note)] : []),
		...(band.legend ? [legendGridBlock(band.legend)] : []),
	];
}

/** De-duplicated union of every page's live attribution, for the (per-page) footer credit. */
function unionAttributions(pages: MapPrintPage[]): string {
	const credits = new Set<string>();
	for (const page of pages) {
		if (page.attribution) {
			credits.add(page.attribution);
		}
	}
	return Array.from(credits).join(', ');
}

/** Branded header: logo (left), optional department/job (center), export date (right), separator line. */
function header(opts: MapPrintPdfOptions, includeDeptJob: boolean): Content[] {
	const content: Content[] = [];

	if (opts.logoSvg) {
		content.push({ svg: opts.logoSvg, width: LOGO_WIDTH, absolutePosition: { x: MARGIN, y: HEADER_Y + 8 } });
	}

	if (includeDeptJob) {
		content.push({
			stack: [
				labelValue(T.TOPO_PRINT_HEADER_DEPARTMENT, opts.departmentName, 0),
				labelValue(T.TOPO_PRINT_HEADER_JOB, opts.jobName, 4),
			],
			absolutePosition: { x: MARGIN + LOGO_WIDTH + 20, y: HEADER_Y + 8 },
			width: PAGE_WIDTH - MARGIN - 150 - (MARGIN + LOGO_WIDTH + 20),
		});
	}

	content.push({
		stack: [
			{
				text: Translate(T.TOPO_PRINT_HEADER_EXPORT_DATE),
				bold: true,
				fontSize: 9,
				color: DATE_LABEL_COLOR,
				alignment: 'right',
			},
			{ text: displayDate(), bold: true, fontSize: 10, color: VALUE_COLOR, alignment: 'right', margin: [0, 3, 0, 0] },
		],
		absolutePosition: { x: PAGE_WIDTH - MARGIN - 150, y: HEADER_Y + 8 },
		width: 150,
	});

	content.push({
		absolutePosition: { x: MARGIN, y: SEPARATOR_Y },
		canvas: [
			{ type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH - MARGIN * 2, y2: 0, lineWidth: 1, lineColor: SEPARATOR_COLOR },
		],
	});

	return content;
}

function labelValue(label: T, value: string, marginTop: number): Content {
	return {
		text: [
			{ text: `${Translate(label)}:  `, bold: true, color: LABEL_COLOR },
			{ text: value, bold: true, color: VALUE_COLOR },
		],
		fontSize: 10,
		margin: [0, marginTop, 0, 0],
	};
}

/** Grey outline frames around the map and whichever bottom-band panels are present. */
function boxes(fit: FittedMap, band: BandLayout): Content {
	const canvas: CanvasElement[] = [rectOutline(fit.x, fit.y, fit.width, fit.height)];
	if (band.note) {
		canvas.push(rectOutline(band.note.x, band.note.y, band.note.width, band.note.height));
	}
	if (band.legend) {
		canvas.push(rectOutline(band.legend.x, band.legend.y, band.legend.width, band.legend.height));
	}
	return { absolutePosition: { x: 0, y: 0 }, canvas };
}

function rectOutline(x: number, y: number, w: number, h: number): CanvasElement {
	return { type: 'rect', x, y, w, h, r: BOX_RADIUS, lineWidth: 1, lineColor: BOX_COLOR };
}

interface FittedMap {
	x: number;
	y: number;
	width: number;
	height: number;
}

/** Scale the captured image to fit the given map area, preserving aspect ratio and centering it. */
function fitMap(area: FittedMap, imageWidth: number, imageHeight: number): FittedMap {
	const scale = Math.min(area.width / imageWidth, area.height / imageHeight);
	const width = imageWidth * scale;
	const height = imageHeight * scale;
	return {
		x: area.x + (area.width - width) / 2,
		y: area.y + (area.height - height) / 2,
		width,
		height,
	};
}

/** Compass-style north arrow in the upper-right corner of the map: a translucent circular plate, an
 *  upward arrow, and an "N" at the top. */
function northArrow(fit: FittedMap): Content[] {
	const diameter = 34;
	const radius = diameter / 2;
	const x = fit.x + fit.width - 12 - diameter;
	const y = fit.y + 12;

	return [
		{
			absolutePosition: { x, y },
			canvas: [
				{ type: 'ellipse', x: radius, y: radius, r1: radius, r2: radius, color: '#ffffff', fillOpacity: 0.8 },
				{
					type: 'polyline',
					closePath: true,
					color: '#000000',
					points: [
						{ x: radius, y: 13 },
						{ x: radius + 6, y: 27 },
						{ x: radius, y: 23 },
						{ x: radius - 6, y: 27 },
					],
				},
			],
		},
		// `alignment: 'center'` is ignored alongside `absolutePosition`, so center the glyph by hand:
		// offset the plate center (`radius`) by ~half the width of a bold "N" at this font size.
		{ text: 'N', absolutePosition: { x: x + radius - 3.25, y: y + 1 }, fontSize: 9, bold: true },
	];
}

/** Note panel content (heading + text) in the bottom band. Wrapped in a fixed-width column so the
 *  note text wraps inside the panel — a plain text leaf under `absolutePosition` ignores `width`. */
function noteBlock(note: string, panel: Panel): Content {
	return {
		absolutePosition: { x: panel.x + PANEL_PAD, y: panel.y + PANEL_PAD },
		columns: [
			{
				width: panel.width - PANEL_PAD * 2,
				stack: [
					{
						text: Translate(T.TOPO_PRINT_NOTE_TITLE).toUpperCase(),
						bold: true,
						fontSize: 9,
						color: HEADING_COLOR,
						margin: [0, 0, 0, 5],
					},
					{ text: note, fontSize: 8, color: VALUE_COLOR },
				],
			},
		],
	};
}

/** Legend grid content, positioned within the given bottom-band panel. Wrapped in a fixed-width
 *  column (like the note) so the grid stays inside the panel under `absolutePosition`. */
function legendGridBlock(panel: Panel): Content {
	return {
		absolutePosition: { x: panel.x + PANEL_PAD, y: panel.y + PANEL_PAD },
		columns: [{ width: panel.width - PANEL_PAD * 2, ...buildLegendGrid() }],
	};
}

function buildFilename(jobName: string): string {
	const safeName = (jobName || 'map').replace(/[\\/:*?"<>|]+/g, '_');
	return `${safeName}_map_${today()}.pdf`;
}

/** Local-time `YYYY-MM-DD` for the filename. Uses en-CA (ISO order) rather than en-US, which the
 *  filename format requires — en-US would produce slash-separated `MM/DD/YYYY`. */
function today(): string {
	return new Date().toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** Human-readable export date, e.g. "June 2, 2026". */
function displayDate(): string {
	return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Static Google data credit, kept verbatim per the Maps Terms of Service. */
function googleDataCredit(): string {
	return `Map data ©${new Date().getFullYear()} Google`;
}

/** Footer credit: the static data credit plus the live credit scraped from the map, de-duplicated —
 *  on roadmap the live credit is itself "Map data ©… Google", which would otherwise print twice. */
function footerAttribution(liveCredit: string): string {
	const parts = [googleDataCredit()];
	if (liveCredit && !parts.includes(liveCredit)) {
		parts.push(liveCredit);
	}
	return parts.join('   ·   ');
}
